import { LitElement, html, PropertyValueMap } from 'lit';
import { customElement, query, property, state } from 'lit/decorators.js';
import { styleMap } from 'lit/directives/style-map.js';

import { emit } from '@amityeko/pbc-core/dist/utilities/event';
import { withAuth } from '@amityeko/pbc-core/dist/providers/auth/consumer-mixin';

import editIcon from '@amityeko/design-system/dist/assets/icons/edit';

import '@amityeko/design-system/dist/elements/icon';
import '@amityeko/design-system/dist/elements/avatar';

import { ImageData, ImageKey } from '../image-layout/types';
import { LayoutList } from '../image-layout-list/types';

import styles from './styles';

/**
 * @dependency amityeko-avatar
 * @dependency amityeko-icon
 */
@customElement('rnr-image-layout-content')
export default class RnRImageLayoutContent extends withAuth(LitElement) {
  static styles = styles;

  @query('#box__one') boxOne!: HTMLElement;
  @query('#box__two') boxTwo!: HTMLElement;
  @query('#box__three') boxThree!: HTMLElement;
  @query('#box__four') boxFour!: HTMLElement;

  @property()
  currentLayout: LayoutList = LayoutList.SQUARE;

  @property({ type: Number })
  imageCount: number = 1;

  @property({ type: Boolean })
  isViewOnly: boolean = false;

  @property()
  width?: string;

  @property()
  height?: string;

  @property({ type: Array })
  imageKeys: ImageKey[] = [];

  @state()
  imageData: ImageData[] = [];

  public reset() {
    this.imageData = [];
  }

  protected updated(changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>) {
    if (this.isViewOnly && changedProperties.has('imageKeys')) {
      this._getImageUrls();
    }
  }

  private async _getImageUrls() {
    if (!this.imageKeys || !this.auth?.http) return;

    for await (const [index, image] of this.imageKeys.entries()) {
      const imageURL = await this.auth.http.media(`/api/v1/media/images/${image.key}`);
      this._setBackgroundImage(index, imageURL);
    }
  }

  private _setBackgroundImage(index: number, url: string) {
    switch (index) {
      case 0:
        this.boxOne.style.background = `url(${url}) var(--amityeko-color-gray-8) no-repeat center center / cover`;
        this.boxOne.style.setProperty('--private-content-one', 'none');
        break;
      case 1:
        this.boxTwo.style.background = `url(${url}) var(--amityeko-color-gray-8) no-repeat center center / cover`;
        this.boxTwo.style.setProperty('--private-content-two', 'none');
        break;
      case 2:
        this.boxThree.style.background = `url(${url}) var(--amityeko-color-gray-8) no-repeat center center / cover`;
        this.boxThree.style.setProperty('--private-content-three', 'none');
        break;
      case 3:
        this.boxFour.style.background = `url(${url}) var(--amityeko-color-gray-8) no-repeat center center / cover`;
        this.boxFour.style.setProperty('--private-content-four', 'none');
        break;
      default:
        break;
    }
  }

  private _handleFile(index: number, e: any) {
    if (this.isViewOnly) return;
    if (e.target.files[0]) {
      const url = URL.createObjectURL(e.target.files[0]);
      this._setBackgroundImage(index, url);

      this.imageData[index] = { file: e.target.files[0] };
      emit(this, 'rnr-change-file', {
        detail: {
          imageData: this.imageData,
        },
      });
      this.requestUpdate();
    }
  }

  private _renderLayoutSquare() {
    return html`
      <div
        class="layout layout--square"
        style=${styleMap({
          '--private-layout-width': this.width,
          '--private-layout-height': this.height,
        })}
      >
        <label
          class="layout__label"
          style=${styleMap({
            '--private-cursor': this.isViewOnly ? 'default' : 'pointer',
          })}
          for="file1"
        >
          <input
            type="file"
            name="file"
            id="file1"
            accept="image/png,image/jpeg"
            ?disabled=${this.isViewOnly}
            @change=${(e: any) => this._handleFile(0, e)}
          />
          <div id="box__one" class="layout__box layout__box--one">
            ${this.imageData[0]
              ? html`
                  <amityeko-avatar
                    .shape=${'circle'}
                    .avatarSize=${'36px'}
                    .customBgColor=${'rgba(0, 0, 0, 0.5)'}
                  >
                    <amityeko-icon slot="avatar-icon" iconSize="18px" color="#ffffff"
                      >${editIcon}</amityeko-icon
                    >
                  </amityeko-avatar>
                `
              : null}
          </div>
        </label>
      </div>
    `;
  }

  private _renderLayoutCircle() {
    return html`
      <div
        class="layout layout--circle"
        style=${styleMap({
          '--private-layout-width': this.width,
          '--private-layout-height': this.height,
        })}
      >
        <label
          class="layout__label"
          style=${styleMap({
            '--private-cursor': this.isViewOnly ? 'default' : 'pointer',
          })}
          for="file1"
        >
          <input
            type="file"
            name="file"
            id="file1"
            ?disabled=${this.isViewOnly}
            accept="image/png,image/jpeg"
            @change=${(e: any) => this._handleFile(0, e)}
          />
          <div id="box__one" class="layout__box layout__box--one">
            ${this.imageData[0]
              ? html`
                  <amityeko-avatar
                    .shape=${'circle'}
                    .avatarSize=${'36px'}
                    .customBgColor=${'rgba(0, 0, 0, 0.5)'}
                  >
                    <amityeko-icon slot="avatar-icon" iconSize="18px" color="#ffffff"
                      >${editIcon}</amityeko-icon
                    >
                  </amityeko-avatar>
                `
              : null}
          </div>
        </label>
      </div>
    `;
  }

  private _renderLayoutSquare3H() {
    return html`
      <div
        class="layout layout--square-3h"
        style=${styleMap({
          '--private-layout-width': this.width,
          '--private-layout-height': this.height,
        })}
      >
        <label class="layout__label" for="file1">
          <input
            type="file"
            name="file"
            id="file1"
            ?disabled=${this.isViewOnly}
            accept="image/png,image/jpeg"
            @change=${(e: any) => this._handleFile(0, e)}
          />
          <div
            id="box__one"
            style=${styleMap({
              '--private-cursor': this.isViewOnly ? 'default' : 'pointer',
            })}
            class="layout__box layout__box--one"
          >
            ${this.imageData[0]
              ? html`
                  <amityeko-avatar
                    .shape=${'circle'}
                    .avatarSize=${'36px'}
                    .customBgColor=${'rgba(0, 0, 0, 0.5)'}
                  >
                    <amityeko-icon slot="avatar-icon" iconSize="18px" color="#ffffff"
                      >${editIcon}</amityeko-icon
                    >
                  </amityeko-avatar>
                `
              : null}
          </div>
        </label>
        <label class="layout__label" for="file2">
          <input
            type="file"
            name="file"
            id="file2"
            ?disabled=${this.isViewOnly}
            accept="image/png,image/jpeg"
            @change=${(e: any) => this._handleFile(1, e)}
          />
          <div
            id="box__two"
            style=${styleMap({
              '--private-cursor': this.isViewOnly ? 'default' : 'pointer',
            })}
            class="layout__box layout__box--two"
          >
            ${this.imageData[1]
              ? html`
                  <amityeko-avatar
                    .shape=${'circle'}
                    .avatarSize=${'36px'}
                    .customBgColor=${'rgba(0, 0, 0, 0.5)'}
                  >
                    <amityeko-icon slot="avatar-icon" iconSize="18px" color="#ffffff"
                      >${editIcon}</amityeko-icon
                    >
                  </amityeko-avatar>
                `
              : null}
          </div>
        </label>
        <label class="layout__label" for="file3">
          <input
            type="file"
            name="file"
            id="file3"
            ?disabled=${this.isViewOnly}
            accept="image/png,image/jpeg"
            @change=${(e: any) => this._handleFile(2, e)}
          />
          <div
            id="box__three"
            style=${styleMap({
              '--private-cursor': this.isViewOnly ? 'default' : 'pointer',
            })}
            class="layout__box layout__box--three"
          >
            ${this.imageData[2]
              ? html`
                  <amityeko-avatar
                    .shape=${'circle'}
                    .avatarSize=${'36px'}
                    .customBgColor=${'rgba(0, 0, 0, 0.5)'}
                  >
                    <amityeko-icon slot="avatar-icon" iconSize="18px" color="#ffffff"
                      >${editIcon}</amityeko-icon
                    >
                  </amityeko-avatar>
                `
              : null}
          </div>
        </label>
      </div>
    `;
  }

  private _renderLayoutSquare1H2S() {
    return html`
      <div
        class="layout layout--square-1h2s"
        style=${styleMap({
          '--private-layout-width': this.width,
          '--private-layout-height': this.height,
        })}
      >
        <label class="layout__label" for="file1">
          <input
            type="file"
            name="file"
            id="file1"
            ?disabled=${this.isViewOnly}
            accept="image/png,image/jpeg"
            @change=${(e: any) => this._handleFile(0, e)}
          />
          <div
            id="box__one"
            style=${styleMap({
              '--private-cursor': this.isViewOnly ? 'default' : 'pointer',
            })}
            class="layout__box layout__box--one"
          >
            ${this.imageData[0]
              ? html`
                  <amityeko-avatar
                    .shape=${'circle'}
                    .avatarSize=${'36px'}
                    .customBgColor=${'rgba(0, 0, 0, 0.5)'}
                  >
                    <amityeko-icon slot="avatar-icon" iconSize="18px" color="#ffffff"
                      >${editIcon}</amityeko-icon
                    >
                  </amityeko-avatar>
                `
              : null}
          </div>
        </label>
        <div class="layout__row__two">
          <label class="layout__label" for="file2">
            <input
              type="file"
              name="file"
              id="file2"
              accept="image/png,image/jpeg"
              ?disabled=${this.isViewOnly}
              @change=${(e: any) => this._handleFile(1, e)}
            />
            <div
              id="box__two"
              style=${styleMap({
                '--private-cursor': this.isViewOnly ? 'default' : 'pointer',
              })}
              class="layout__box layout__box--two"
            >
              ${this.imageData[1]
                ? html`
                    <amityeko-avatar
                      .shape=${'circle'}
                      .avatarSize=${'36px'}
                      .customBgColor=${'rgba(0, 0, 0, 0.5)'}
                    >
                      <amityeko-icon slot="avatar-icon" iconSize="18px" color="#ffffff"
                        >${editIcon}</amityeko-icon
                      >
                    </amityeko-avatar>
                  `
                : null}
            </div>
          </label>
          <label class="layout__label" for="file3">
            <input
              type="file"
              name="file"
              id="file3"
              ?disabled=${this.isViewOnly}
              accept="image/png,image/jpeg"
              @change=${(e: any) => this._handleFile(2, e)}
            />
            <div
              id="box__three"
              style=${styleMap({
                '--private-cursor': this.isViewOnly ? 'default' : 'pointer',
              })}
              class="layout__box layout__box--three"
            >
              ${this.imageData[2]
                ? html`
                    <amityeko-avatar
                      .shape=${'circle'}
                      .avatarSize=${'36px'}
                      .customBgColor=${'rgba(0, 0, 0, 0.5)'}
                    >
                      <amityeko-icon slot="avatar-icon" iconSize="18px" color="#ffffff"
                        >${editIcon}</amityeko-icon
                      >
                    </amityeko-avatar>
                  `
                : null}
            </div>
          </label>
        </div>
      </div>
    `;
  }

  private _renderLayoutSquare4S() {
    return html`
      <div
        class="layout layout--square-4s"
        style=${styleMap({
          '--private-layout-width': this.width,
          '--private-layout-height': this.height,
        })}
      >
        <label class="layout__label" for="file1">
          <input
            type="file"
            name="file"
            id="file1"
            ?disabled=${this.isViewOnly}
            accept="image/png,image/jpeg"
            @change=${(e: any) => this._handleFile(0, e)}
          />
          <div
            id="box__one"
            style=${styleMap({
              '--private-cursor': this.isViewOnly ? 'default' : 'pointer',
            })}
            class="layout__box layout__box--one"
          >
            ${this.imageData[0]
              ? html`
                  <amityeko-avatar
                    .shape=${'circle'}
                    .avatarSize=${'36px'}
                    .customBgColor=${'rgba(0, 0, 0, 0.5)'}
                  >
                    <amityeko-icon slot="avatar-icon" iconSize="18px" color="#ffffff"
                      >${editIcon}</amityeko-icon
                    >
                  </amityeko-avatar>
                `
              : null}
          </div>
        </label>
        <label class="layout__label" for="file2">
          <input
            type="file"
            name="file"
            id="file2"
            ?disabled=${this.isViewOnly}
            accept="image/png,image/jpeg"
            @change=${(e: any) => this._handleFile(1, e)}
          />
          <div
            id="box__two"
            style=${styleMap({
              '--private-cursor': this.isViewOnly ? 'default' : 'pointer',
            })}
            class="layout__box layout__box--two"
          >
            ${this.imageData[1]
              ? html`
                  <amityeko-avatar
                    .shape=${'circle'}
                    .avatarSize=${'36px'}
                    .customBgColor=${'rgba(0, 0, 0, 0.5)'}
                  >
                    <amityeko-icon slot="avatar-icon" iconSize="18px" color="#ffffff"
                      >${editIcon}</amityeko-icon
                    >
                  </amityeko-avatar>
                `
              : null}
          </div>
        </label>
        <label class="layout__label" for="file3">
          <input
            type="file"
            name="file"
            id="file3"
            ?disabled=${this.isViewOnly}
            accept="image/png,image/jpeg"
            @change=${(e: any) => this._handleFile(2, e)}
          />
          <div
            id="box__three"
            style=${styleMap({
              '--private-cursor': this.isViewOnly ? 'default' : 'pointer',
            })}
            class="layout__box layout__box--three"
          >
            ${this.imageData[2]
              ? html`
                  <amityeko-avatar
                    .shape=${'circle'}
                    .avatarSize=${'36px'}
                    .customBgColor=${'rgba(0, 0, 0, 0.5)'}
                  >
                    <amityeko-icon slot="avatar-icon" iconSize="18px" color="#ffffff"
                      >${editIcon}</amityeko-icon
                    >
                  </amityeko-avatar>
                `
              : null}
          </div>
        </label>
        <label class="layout__label" for="file4">
          <input
            type="file"
            name="file"
            id="file4"
            ?disabled=${this.isViewOnly}
            accept="image/png,image/jpeg"
            @change=${(e: any) => this._handleFile(3, e)}
          />
          <div
            id="box__four"
            style=${styleMap({
              '--private-cursor': this.isViewOnly ? 'default' : 'pointer',
            })}
            class="layout__box layout__box--four"
          >
            ${this.imageData[3]
              ? html`
                  <amityeko-avatar
                    .shape=${'circle'}
                    .avatarSize=${'36px'}
                    .customBgColor=${'rgba(0, 0, 0, 0.5)'}
                  >
                    <amityeko-icon slot="avatar-icon" iconSize="18px" color="#ffffff"
                      >${editIcon}</amityeko-icon
                    >
                  </amityeko-avatar>
                `
              : null}
          </div>
        </label>
      </div>
    `;
  }

  private _renderLayoutSquare1V3S() {
    return html`
      <div
        class="layout layout--square-1v3s"
        style=${styleMap({
          '--private-layout-width': this.width,
          '--private-layout-height': this.height,
        })}
      >
        <label class="layout__label" for="file1">
          <input
            type="file"
            name="file"
            id="file1"
            ?disabled=${this.isViewOnly}
            accept="image/png,image/jpeg"
            @change=${(e: any) => this._handleFile(0, e)}
          />
          <div
            id="box__one"
            style=${styleMap({
              '--private-cursor': this.isViewOnly ? 'default' : 'pointer',
            })}
            class="layout__box layout__box--one"
          >
            ${this.imageData[0]
              ? html`
                  <amityeko-avatar
                    .shape=${'circle'}
                    .avatarSize=${'36px'}
                    .customBgColor=${'rgba(0, 0, 0, 0.5)'}
                  >
                    <amityeko-icon slot="avatar-icon" iconSize="18px" color="#ffffff"
                      >${editIcon}</amityeko-icon
                    >
                  </amityeko-avatar>
                `
              : null}
          </div>
        </label>
        <div class="layout__col__two">
          <label class="layout__label" for="file2">
            <input
              type="file"
              name="file"
              id="file2"
              ?disabled=${this.isViewOnly}
              accept="image/png,image/jpeg"
              @change=${(e: any) => this._handleFile(1, e)}
            />
            <div
              id="box__two"
              style=${styleMap({
                '--private-cursor': this.isViewOnly ? 'default' : 'pointer',
              })}
              class="layout__box layout__box--two"
            >
              ${this.imageData[1]
                ? html`
                    <amityeko-avatar
                      .shape=${'circle'}
                      .avatarSize=${'36px'}
                      .customBgColor=${'rgba(0, 0, 0, 0.5)'}
                    >
                      <amityeko-icon slot="avatar-icon" iconSize="18px" color="#ffffff"
                        >${editIcon}</amityeko-icon
                      >
                    </amityeko-avatar>
                  `
                : null}
            </div>
          </label>
          <label class="layout__label" for="file3">
            <input
              type="file"
              name="file"
              id="file3"
              ?disabled=${this.isViewOnly}
              accept="image/png,image/jpeg"
              @change=${(e: any) => this._handleFile(2, e)}
            />
            <div
              id="box__three"
              style=${styleMap({
                '--private-cursor': this.isViewOnly ? 'default' : 'pointer',
              })}
              class="layout__box layout__box--three"
            >
              ${this.imageData[2]
                ? html`
                    <amityeko-avatar
                      .shape=${'circle'}
                      .avatarSize=${'36px'}
                      .customBgColor=${'rgba(0, 0, 0, 0.5)'}
                    >
                      <amityeko-icon slot="avatar-icon" iconSize="18px" color="#ffffff"
                        >${editIcon}</amityeko-icon
                      >
                    </amityeko-avatar>
                  `
                : null}
            </div>
          </label>
          <label class="layout__label" for="file4">
            <input
              type="file"
              name="file"
              id="file4"
              ?disabled=${this.isViewOnly}
              accept="image/png,image/jpeg"
              @change=${(e: any) => this._handleFile(3, e)}
            />
            <div
              id="box__four"
              style=${styleMap({
                '--private-cursor': this.isViewOnly ? 'default' : 'pointer',
              })}
              class="layout__box layout__box--four"
            >
              ${this.imageData[3]
                ? html`
                    <amityeko-avatar
                      .shape=${'circle'}
                      .avatarSize=${'36px'}
                      .customBgColor=${'rgba(0, 0, 0, 0.5)'}
                    >
                      <amityeko-icon slot="avatar-icon" iconSize="18px" color="#ffffff"
                        >${editIcon}</amityeko-icon
                      >
                    </amityeko-avatar>
                  `
                : null}
            </div>
          </label>
        </div>
      </div>
    `;
  }

  protected render() {
    return html`
      ${this.currentLayout === 'square' ? this._renderLayoutSquare() : null}
      ${this.currentLayout === 'circle' ? this._renderLayoutCircle() : null}
      ${this.currentLayout === 'square-3h' ? this._renderLayoutSquare3H() : null}
      ${this.currentLayout === 'square-1h2s' ? this._renderLayoutSquare1H2S() : null}
      ${this.currentLayout === 'square-4s' ? this._renderLayoutSquare4S() : null}
      ${this.currentLayout === 'square-1v3s' ? this._renderLayoutSquare1V3S() : null}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'rnr-image-layout-content': RnRImageLayoutContent;
  }
}
